import { useMemo } from 'react'
import { ProjectGridField } from '@/types/fields'
import { sumDecimalStrings } from '@/utils/decimal'
import { getFieldsFromProject } from '@/utils/field'
import {
  DUE_DATE_INFO_TYPE_ID,
  INVOICE_DATE_INFO_TYPE_ID,
  INVOICE_GL_ACCOUNT_INFO_TYPE_ID,
  INVOICE_NUMBER_INFO_TYPE_ID,
  INVOICE_TOTAL_INFO_TYPE_ID,
  INVOICE_VENDOR_INFO_TYPE_ID,
  INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID,
  INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID,
  LINE_ITEM_COST_TYPE_INFO_TYPE_ID,
  LINE_ITEM_JOB_INFO_TYPE_ID,
  LINE_ITEM_PHASE_INFO_TYPE_ID,
  LINE_ITEM_QUANTITY_SHIPPED_INFO_TYPE_ID,
  LINE_ITEM_RATE_INFO_TYPE_ID,
  PURCHASE_ORDER_NUMBER_INFO_TYPE_ID,
} from '@/utils/info-types'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useDocumentRowsContext } from '@/components/validation/providers/DocumentRowsProvider'
import { useDocumentRowValuesContext } from '@/components/validation/providers/DocumentRowValuesProvider'
import useGetValueData from '@/components/validation/useGetValueData'

export type LineItem = {
  rowId: string
  amount: string | null
  description: string | null
  jobNumber: string | null
  jobDisplayName?: string
  phaseNumber: string | null
  phaseDisplayName?: string
  glAccountNumber?: string | null
  glAccountDisplayName?: string
  costTypeNumber?: string | null
  costTypeDisplayName?: string
  quantity: string | null
  rate: string | null
}

/** Whether the project has each PO-matching field configured at all — used to hide (not dash-out) fields/columns it doesn't have. */
export type InvoiceFieldAvailability = {
  vendor: boolean
  poNumber: boolean
  invoiceNumber: boolean
  invoiceDate: boolean
  dueDate: boolean
  invoiceTotal: boolean
  description: boolean
  amount: boolean
  job: boolean
  phase: boolean
  glAccount: boolean
  costType: boolean
  quantity: boolean
  rate: boolean
}

export type DocumentInvoiceData = {
  /** False until the document's rows and their values have loaded (no base row yet, or row values still fetching). */
  isReady: boolean
  vendorName: string | null
  vendorId: string | null
  vendorField: ProjectGridField | null
  purchaseOrderNumber: string | null
  invoiceNumber: string | null
  invoiceDate: string | null
  dueDate: string | null
  invoiceTotal: string | null
  /** Not the invoice's own total — use invoiceTotal for that. */
  lineItemAmountSum: string | null
  lineItems: LineItem[]
  availability: InvoiceFieldAvailability
}

const NOT_READY_AVAILABILITY: InvoiceFieldAvailability = {
  vendor: false,
  poNumber: false,
  invoiceNumber: false,
  invoiceDate: false,
  dueDate: false,
  invoiceTotal: false,
  description: false,
  amount: false,
  job: false,
  phase: false,
  glAccount: false,
  costType: false,
  quantity: false,
  rate: false,
}

const NOT_READY_DATA: DocumentInvoiceData = {
  isReady: false,
  vendorName: null,
  vendorId: null,
  vendorField: null,
  purchaseOrderNumber: null,
  invoiceNumber: null,
  invoiceDate: null,
  dueDate: null,
  invoiceTotal: null,
  lineItemAmountSum: null,
  lineItems: [],
  availability: NOT_READY_AVAILABILITY,
}

export default function useDocumentInvoiceData(): DocumentInvoiceData {
  const { project } = useProjectContext()
  const { baseRow, rowsByGrid } = useDocumentRowsContext()
  const { isLoading: isLoadingRowValues } = useDocumentRowValuesContext()
  const getValueData = useGetValueData()

  return useMemo(() => {
    const baseRowId = baseRow?.id
    if (!baseRowId || isLoadingRowValues) {
      return NOT_READY_DATA
    }
    const allFields = getFieldsFromProject(project)
    const fieldsByInfoTypeId = allFields.reduce<
      Record<string, ProjectGridField>
    >((acc, field) => {
      if (!field.info_type_id) return acc
      acc[field.info_type_id] = field
      return acc
    }, {})

    // Find the base-level fields — a project isn't guaranteed to have every one of these configured.
    const vendorField = fieldsByInfoTypeId[INVOICE_VENDOR_INFO_TYPE_ID]
    const poNumberField = fieldsByInfoTypeId[PURCHASE_ORDER_NUMBER_INFO_TYPE_ID]
    const invoiceNumberField = fieldsByInfoTypeId[INVOICE_NUMBER_INFO_TYPE_ID]
    const invoiceDateField = fieldsByInfoTypeId[INVOICE_DATE_INFO_TYPE_ID]
    const dueDateField = fieldsByInfoTypeId[DUE_DATE_INFO_TYPE_ID]
    const invoiceTotalField = fieldsByInfoTypeId[INVOICE_TOTAL_INFO_TYPE_ID]

    const vendorData = getValueData({ fieldId: vendorField?.id })

    // getValueData returns {} (no `value`) when fieldId is undefined, so coerce
    // to null here to honor the string | null contract these fields declare.
    const vendorName = vendorData.value ?? null
    const purchaseOrderNumber =
      getValueData({ fieldId: poNumberField?.id }).value ?? null
    const invoiceNumber =
      getValueData({ fieldId: invoiceNumberField?.id }).value ?? null
    const invoiceDate =
      getValueData({ fieldId: invoiceDateField?.id }).value ?? null
    const dueDate = getValueData({ fieldId: dueDateField?.id }).value ?? null
    const invoiceTotal =
      getValueData({ fieldId: invoiceTotalField?.id }).value ?? null

    // Find the line items table fields — same caveat, none are guaranteed to exist.
    const amountField =
      fieldsByInfoTypeId[INVOICE_LINE_ITEM_AMOUNT_INFO_TYPE_ID]
    const descriptionField =
      fieldsByInfoTypeId[INVOICE_LINE_ITEM_DESCRIPTION_INFO_TYPE_ID]
    const jobField = fieldsByInfoTypeId[LINE_ITEM_JOB_INFO_TYPE_ID]
    const phaseField = fieldsByInfoTypeId[LINE_ITEM_PHASE_INFO_TYPE_ID]
    const glAccountField = fieldsByInfoTypeId[INVOICE_GL_ACCOUNT_INFO_TYPE_ID]
    const costTypeField = fieldsByInfoTypeId[LINE_ITEM_COST_TYPE_INFO_TYPE_ID]
    const quantityField =
      fieldsByInfoTypeId[LINE_ITEM_QUANTITY_SHIPPED_INFO_TYPE_ID]
    const rateField = fieldsByInfoTypeId[LINE_ITEM_RATE_INFO_TYPE_ID]

    // Assuming all line item fields belong to the same grid, so we can use any configured one to get the rows
    const lineItemGridField =
      descriptionField ||
      amountField ||
      quantityField ||
      rateField ||
      jobField ||
      phaseField ||
      glAccountField ||
      costTypeField
    const lineItemRows =
      rowsByGrid[lineItemGridField?.project_grid_id || ''] || []
    const lineItems: LineItem[] = lineItemRows.map((row) => {
      const jobFieldValue = getValueData({
        fieldId: jobField?.id,
        rowId: row.id,
      })
      const phaseFieldValue = getValueData({
        fieldId: phaseField?.id,
        rowId: row.id,
      })
      const glAccountFieldValue = getValueData({
        fieldId: glAccountField?.id,
        rowId: row.id,
      })
      const costTypeFieldValue = getValueData({
        fieldId: costTypeField?.id,
        rowId: row.id,
      })
      return {
        rowId: row.id,
        amount:
          getValueData({ fieldId: amountField?.id, rowId: row.id }).value ??
          null,
        description:
          getValueData({ fieldId: descriptionField?.id, rowId: row.id })
            .value ?? null,
        jobNumber: jobFieldValue.jsonValue?.num || null,
        jobDisplayName: jobFieldValue.jsonValue?.pd_display_name,
        phaseNumber: phaseFieldValue.jsonValue?.num || null,
        phaseDisplayName: phaseFieldValue.jsonValue?.pd_display_name,
        glAccountNumber: glAccountFieldValue.jsonValue?.num || null,
        glAccountDisplayName: glAccountFieldValue.jsonValue?.pd_display_name,
        costTypeNumber: costTypeFieldValue.jsonValue?.num || null,
        costTypeDisplayName: costTypeFieldValue.jsonValue?.pd_display_name,
        quantity:
          getValueData({ fieldId: quantityField?.id, rowId: row.id }).value ??
          null,
        rate:
          getValueData({ fieldId: rateField?.id, rowId: row.id }).value ?? null,
      }
    })

    const lineItemAmountSum = sumDecimalStrings(
      lineItems.map((li) => li.amount),
    )

    return {
      isReady: true,
      vendorName,
      vendorId: vendorData.jsonValue?.entity_id || null,
      vendorField: vendorField || null,
      purchaseOrderNumber,
      invoiceNumber,
      invoiceDate,
      dueDate,
      invoiceTotal,
      lineItemAmountSum,
      lineItems,
      availability: {
        vendor: !!vendorField,
        poNumber: !!poNumberField,
        invoiceNumber: !!invoiceNumberField,
        invoiceDate: !!invoiceDateField,
        dueDate: !!dueDateField,
        invoiceTotal: !!invoiceTotalField,
        description: !!descriptionField,
        amount: !!amountField,
        job: !!jobField,
        phase: !!phaseField,
        glAccount: !!glAccountField,
        costType: !!costTypeField,
        quantity: !!quantityField,
        rate: !!rateField,
      },
    }
  }, [baseRow?.id, isLoadingRowValues, project, getValueData, rowsByGrid])
}
